/*
 * Grouping / formatting helpers for the warrant import preview dialog.
 * The backend preValidate response (`preview`) is a flat list of one row per
 * warrant; the dialog shows one summary row per fiscal-year + fund, expandable
 * to the warrant detail rows.
 */

// Worst-first display precedence: the fund summary row shows the most severe
// status of the warrants it contains.
const STATUS_RANK = {
  dateError: 6,
  seqError: 5,
  negBalance: 4,
  fundMissing: 3,
  accountMissing: 3,
  deptMissing: 3,
  skipDup: 2,
  ok: 1,
};

// Statuses that do not block a warrant from being created.
const AUTO_CREATABLE = ['ok', 'fundMissing', 'accountMissing', 'deptMissing', 'negBalance'];

export const toAmount = (value) => Number(value) || 0;

export const sumPreviewAmounts = (rows) => (rows || [])
  .reduce((total, row) => total + toAmount(row.amount), 0);

export const previewStatusColor = (status) => {
  switch (status) {
    case 'ok': return 'green';
    case 'skipDup': return 'grey';
    case 'seqError': return 'red';
    case 'fundMissing': return 'orange';
    case 'accountMissing': return 'orange';
    case 'deptMissing': return 'orange';
    case 'negBalance': return 'red';
    case 'dateError': return 'red darken-3';
    default: return 'grey';
  }
};

export const previewStatusLabel = (status) => {
  switch (status) {
    case 'ok': return 'Will create';
    case 'skipDup': return 'Skip (duplicate)';
    case 'seqError': return 'Sequence error';
    case 'fundMissing': return 'Will create + auto-gen fund';
    case 'accountMissing': return 'Will create + auto-gen account';
    case 'deptMissing': return 'Will create + auto-gen dept';
    case 'negBalance': return 'Negative balance';
    case 'dateError': return 'Invalid date';
    default: return status;
  }
};

export const willCreatePreviewRows = (rows, options = {}) => {
  const { allowSeqErrorImport, hasBadDates } = options;
  if (hasBadDates) return [];
  const creatable = allowSeqErrorImport ? AUTO_CREATABLE.concat(['seqError']) : AUTO_CREATABLE;
  return (rows || []).filter((row) => creatable.includes(row.status));
};

const statusRank = (status) => STATUS_RANK[status] || 0;

const worstStatus = (rows) => rows.reduce((worst, row) => (
  statusRank(row.status) > statusRank(worst) ? row.status : worst
), 'ok');

// Numeric compare when both sides parse as numbers (so "7206" sorts after "830"),
// falling back to a lexical compare for non-numeric identifiers.
const numericThenLexicalCompare = (labelA, valueA, labelB, valueB) => {
  if (Number.isNaN(valueA) || Number.isNaN(valueB)) return labelA.localeCompare(labelB);
  return valueA - valueB;
};

const warrantRange = (rows) => {
  const numbered = rows
    .map((row) => ({ label: `${row.warrant == null ? '' : row.warrant}`, value: Number(row.warrant) }))
    .filter((w) => w.label !== '');
  if (numbered.length === 0) return '';
  const sorted = numbered.slice().sort((a, b) => numericThenLexicalCompare(a.label, a.value, b.label, b.value));
  const first = sorted[0].label;
  const last = sorted[sorted.length - 1].label;
  const range = first === last ? first : `${first}-${last}`;
  return `${range}(${numbered.length})`;
};

const groupReason = (rows, status) => {
  const reasons = rows
    .filter((row) => row.status === status && row.reason)
    .map((row) => row.reason);
  const distinct = Array.from(new Set(reasons));
  const otherIssues = Array.from(new Set(rows
    .filter((row) => row.status !== status && row.status !== 'ok')
    .map((row) => row.status)));
  const primary = distinct.length > 0 ? distinct[0] : '';
  if (otherIssues.length === 0) return primary;
  const suffix = `+${otherIssues.length} other issue${otherIssues.length === 1 ? '' : 's'}`;
  return primary ? `${primary} (${suffix})` : suffix;
};

/*
 * One summary row per fiscal year + fund, each carrying its warrant detail rows.
 */
export const buildFundGroups = (preview) => {
  const byKey = new Map();
  (preview || []).forEach((row) => {
    const key = `${row.fiscalYear}|${row.fund}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key, fiscalYear: row.fiscalYear, fund: row.fund, rows: [],
      });
    }
    byKey.get(key).rows.push(row);
  });
  return Array.from(byKey.values())
    .map((group) => {
      const status = worstStatus(group.rows);
      return {
        ...group,
        rowType: 'group',
        department: '',
        account: '',
        warrantDisplay: warrantRange(group.rows),
        amount: sumPreviewAmounts(group.rows),
        status,
        reason: groupReason(group.rows, status),
      };
    })
    .sort((a, b) => {
      const fy = Number(a.fiscalYear) - Number(b.fiscalYear);
      if (fy !== 0 && !Number.isNaN(fy)) return fy;
      return numericThenLexicalCompare(`${a.fund}`, Number(a.fund), `${b.fund}`, Number(b.fund));
    });
};

/*
 * Flatten the groups into data-table rows, splicing in the detail rows of the
 * groups the user has expanded.
 */
export const flattenPreviewRows = (groups, expandedKeys) => {
  const expanded = expandedKeys || [];
  const rows = [];
  (groups || []).forEach((group) => {
    rows.push(group);
    if (expanded.includes(group.key)) {
      group.rows.forEach((row, idx) => {
        rows.push({
          ...row,
          key: `${group.key}|detail|${idx}`,
          rowType: 'detail',
          warrantDisplay: row.warrant,
        });
      });
    }
  });
  return rows;
};

export default {};
